import { useState } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { trpc } from '../lib/trpc';
import { setToken, setRefreshToken } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { Loader2 } from 'lucide-react';

export function LoginPage() {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [credential, setCredential] = useState('');

  const verifyOtp = trpc.auth.verifyOtp.useMutation({
    onSuccess: (data) => {
      if (data.setupRequired && data.setupToken) {
        sessionStorage.setItem('setupToken', data.setupToken);
        toast.success('Login successful. Please complete your setup.');
        navigate('/setup');
      } else {
        toast.success('Welcome back!');
        navigate('/dashboard');
      }
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to verify OTP');
    },
  });

  const login = trpc.auth.login.useMutation({
    onSuccess: (data) => {
      if (data.accessToken && data.refreshToken) {
        setToken(data.accessToken);
        setRefreshToken(data.refreshToken);
        toast.success('Welcome back!');
        navigate('/dashboard');
      }
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to login');
    },
  });

  const isLoading = verifyOtp.isPending || login.isPending;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!username.trim() || !credential.trim()) {
      toast.error('Please enter both username and password/OTP');
      return;
    }

    const trimmedCredential = credential.trim();
    const isOtp = /^\d{6}$/.test(trimmedCredential);

    if (isOtp) {
      verifyOtp.mutate({
        username: username.trim(),
        otp: trimmedCredential,
      });
    } else {
      login.mutate({
        username: username.trim(),
        password: trimmedCredential,
      });
    }
  };

  return (
    <Card className="w-full shadow-lg">
      <CardHeader className="space-y-1">
        <CardTitle className="text-2xl font-bold">Resident Login</CardTitle>
        <CardDescription>
          Enter your username and password or the one-time passcode provided by your administrator.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="username">Username</Label>
            <Input
              id="username"
              placeholder="e.g. GP101"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              disabled={isLoading}
              autoComplete="username"
              autoFocus
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="credential">Password or OTP</Label>
            <Input
              id="credential"
              type="text"
              placeholder="Enter your password or 6-digit OTP"
              value={credential}
              onChange={(e) => setCredential(e.target.value)}
              disabled={isLoading}
              autoComplete="off"
            />
          </div>
          <Button
            type="submit"
            className="w-full"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Verifying...
              </>
            ) : (
              'Login'
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
